import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    grid.setAttribute('product-grid-view', this.#normalizeLayout(value, viewport));
  }

  /**
   * Maps legacy Horizon grid values to Anne Klein column layouts.
   *
   * @param {string | null} value
   * @param {'desktop' | 'mobile'} viewport
   * @returns {string}
   */
  #normalizeLayout(value, viewport) {
    if (viewport === 'desktop') {
      if (value === 'default' || value === 'cols-4' || !value) return 'cols-4';
      if (value === 'zoom-out' || value === 'cols-6') return 'cols-6';
      return 'cols-4';
    }

    return value === 'mobile-single' ? 'mobile-single' : 'default';
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`);
    const defaultLayout = event.matches ? 'cols-4' : 'default';
    const layout = this.#normalizeLayout(storedLayout || defaultLayout, viewport);

    const targetElement = event.matches
      ? this.querySelector(`input[name="grid"][value="${layout}"]`)
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout(layout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
